// Utility functions for package management

export interface ProductPackage {
  id: number;
  productId: number;
  packageType: string;
  weight: string;
  unit: string;
  price: string | null;
  price_ars: string | null;
  isActive: boolean | null;
  createdAt: Date;
  updatedAt: Date;
}

export interface PackageWithImage extends ProductPackage {
  imagePath?: string | null;
}

// Cache for package configurations
let packageConfigCache: Record<string, string | null> = {};
let packageConfigCacheTime: number = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Cache for product packages
let productPackagesCache: Record<number, { data: PackageWithImage[], timestamp: number }> = {};
const PACKAGES_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes (increased for better performance)

// Fetch package type configurations with caching
export async function getPackageTypeConfigs(): Promise<Record<string, string | null>> {
  const now = Date.now();
  
  // Return cached data if still valid
  if (now - packageConfigCacheTime < CACHE_DURATION && Object.keys(packageConfigCache).length > 0) {
    return packageConfigCache;
  }
  
  try {
    const response = await fetch('/api/package-type-configs');
    if (response.ok) {
      const data = await response.json();
      const configMap = data.configs.reduce((acc: Record<string, string | null>, config: any) => {
        acc[config.packageType] = config.imagePath;
        return acc;
      }, {});
      
      packageConfigCache = configMap;
      packageConfigCacheTime = now;
      return configMap;
    }
  } catch (error) {
    console.error('Error fetching package configs:', error);
  }
  
  return {};
}

// Get image path for a specific package type
export async function getImagePathForPackageType(packageType: string): Promise<string | null> {
  const configs = await getPackageTypeConfigs();
  return configs[packageType] || null;
}

// Fetch packages for a product with caching
export async function getProductPackages(productId: number): Promise<PackageWithImage[]> {
  const now = Date.now();
  const startTime = performance.now();
  
  console.log(`[PackageUtils] Fetching packages for product ${productId}...`);
  
  // Check cache first
  if (productPackagesCache[productId] && 
      now - productPackagesCache[productId].timestamp < PACKAGES_CACHE_DURATION) {
    const cacheTime = performance.now() - startTime;
    console.log(`[PackageUtils] Cache hit for product ${productId} (${cacheTime.toFixed(2)}ms) - ${productPackagesCache[productId].data.length} packages`);
    return productPackagesCache[productId].data;
  }
  
  try {
    const fetchStart = performance.now();
    const response = await fetch(`/api/products/${productId}/packages`);
    const fetchTime = performance.now() - fetchStart;
    console.log(`[PackageUtils] API fetch took ${fetchTime.toFixed(2)}ms for product ${productId}`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch packages');
    }
    
    const parseStart = performance.now();
    const packages: ProductPackage[] = await response.json();
    const parseTime = performance.now() - parseStart;
    console.log(`[PackageUtils] JSON parse took ${parseTime.toFixed(2)}ms - received ${packages.length} packages`);
    
    const configStart = performance.now();
    const configs = await getPackageTypeConfigs();
    const configTime = performance.now() - configStart;
    console.log(`[PackageUtils] Config fetch took ${configTime.toFixed(2)}ms`);
    
    // Add image paths to packages
    const packagesWithImages: PackageWithImage[] = packages.map(pkg => ({
      ...pkg,
      imagePath: configs[pkg.packageType] || null
    }));
    
    // Cache the result
    productPackagesCache[productId] = {
      data: packagesWithImages,
      timestamp: now
    };
    
    const totalTime = performance.now() - startTime;
    console.log(`[PackageUtils] Total package fetch for product ${productId}: ${totalTime.toFixed(2)}ms - ${packagesWithImages.length} packages with images`);
    
    return packagesWithImages;
  } catch (error) {
    console.error('Error fetching product packages:', error);
    return [];
  }
}

// Clear package cache for a specific product (call after updates)
export function clearProductPackagesCache(productId: number): void {
  delete productPackagesCache[productId];
}

// Clear all package caches
export function clearAllPackageCaches(): void {
  productPackagesCache = {};
  packageConfigCache = {};
  packageConfigCacheTime = 0;
}

// Format package display text
export function formatPackageText(pkg: ProductPackage): string {
  const weight = parseFloat(pkg.weight);
  const formattedWeight = weight % 1 === 0 ? weight.toString() : weight.toFixed(3);
  return `${pkg.packageType} ${formattedWeight}${pkg.unit}`;
}

// Get package price display
export function getPackagePrice(pkg: ProductPackage): { usd: number | null, ars: number | null } {
  return {
    usd: pkg.price ? parseFloat(pkg.price) : null,
    ars: pkg.price_ars ? parseFloat(pkg.price_ars) : null
  };
}

// Bulk fetch packages for multiple products
export async function getBulkProductPackages(productIds: number[]): Promise<Record<number, PackageWithImage[]>> {
  const startTime = performance.now();
  const uniqueProductIds = [...new Set(productIds)];
  const result: Record<number, PackageWithImage[]> = {};
  const now = Date.now();
  
  console.log(`[PackageUtils] Bulk fetching packages for ${uniqueProductIds.length} products...`);
  
  // Separate cached and uncached product IDs
  const cachedIds: number[] = [];
  const uncachedIds: number[] = [];
  
  for (const productId of uniqueProductIds) {
    if (productPackagesCache[productId] && 
        now - productPackagesCache[productId].timestamp < PACKAGES_CACHE_DURATION) {
      cachedIds.push(productId);
      result[productId] = productPackagesCache[productId].data;
    } else {
      uncachedIds.push(productId);
    }
  }
  
  console.log(`[PackageUtils] Cache hit for ${cachedIds.length} products, fetching ${uncachedIds.length} from API`);
  
  // If we have uncached IDs, fetch them in parallel
  if (uncachedIds.length > 0) {
    try {
      const fetchStart = performance.now();
      
      // Fetch packages for all uncached products in parallel
      const fetchPromises = uncachedIds.map(async (productId) => {
        try {
          const response = await fetch(`/api/products/${productId}/packages`);
          if (!response.ok) {
            console.warn(`Failed to fetch packages for product ${productId}`);
            return { productId, packages: [] };
          }
          const packages: ProductPackage[] = await response.json();
          return { productId, packages };
        } catch (error) {
          console.error(`Error fetching packages for product ${productId}:`, error);
          return { productId, packages: [] };
        }
      });
      
      const fetchResults = await Promise.all(fetchPromises);
      const fetchTime = performance.now() - fetchStart;
      console.log(`[PackageUtils] Parallel API fetches completed in ${fetchTime.toFixed(2)}ms`);
      
      // Get package type configurations once for all packages
      const configStart = performance.now();
      const configs = await getPackageTypeConfigs();
      const configTime = performance.now() - configStart;
      console.log(`[PackageUtils] Config fetch took ${configTime.toFixed(2)}ms`);
      
      // Process results and update cache
      for (const { productId, packages } of fetchResults) {
        const packagesWithImages: PackageWithImage[] = packages.map(pkg => ({
          ...pkg,
          imagePath: configs[pkg.packageType] || null
        }));
        
        // Cache the result
        productPackagesCache[productId] = {
          data: packagesWithImages,
          timestamp: now
        };
        
        result[productId] = packagesWithImages;
      }
      
    } catch (error) {
      console.error('Error in bulk fetch:', error);
      // For any remaining uncached IDs, set empty arrays
      for (const productId of uncachedIds) {
        if (!result[productId]) {
          result[productId] = [];
        }
      }
    }
  }
  
  const totalTime = performance.now() - startTime;
  const totalPackages = Object.values(result).reduce((sum, packages) => sum + packages.length, 0);
  console.log(`[PackageUtils] Bulk fetch completed in ${totalTime.toFixed(2)}ms - ${totalPackages} total packages for ${uniqueProductIds.length} products`);
  
  return result;
}